import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, CheckCircle2, Clock, Coins, Lock, ShieldCheck, Sparkles, Users, Zap } from "lucide-react";
import { PRODUCTS, type ProductSkuId } from "@limeshare/shared";
import { CryptoCheckoutModal } from "../components/CryptoCheckoutModal";
import { TeamCostCalculator } from "../components/TeamCostCalculator";

export function ProductTeams() {
  const [checkoutSku, setCheckoutSku] = useState<ProductSkuId | null>(null);
  return (
    <article className="panel legal" style={{ maxWidth: 960, margin: "0 auto" }}>
      <div style={{ textAlign: "center", maxWidth: 680, margin: "0 auto 32px" }}>
        <span className="badge" style={{ marginBottom: 12 }}>LimeShare Teams · Zero Per-Seat Fees</span>
        <h1 style={{ fontSize: "clamp(30px, 4.5vw, 46px)", fontWeight: 900, margin: "0 0 12px", letterSpacing: "-0.03em" }}>
          One Price for the Whole Office: <span style={{ color: "var(--lime)" }}>25 Members, 2 TB Shared Pool!</span>
        </h1>
        <p className="hint" style={{ fontSize: 16 }}>
          Stop paying $20/month per employee. Empower your entire studio, law firm, or agency with a 2 TB zero-knowledge encrypted pool for one flat yearly price of $149.
        </p>
        <div style={{ marginTop: 22 }}>
          <button className="btn" style={{ padding: "14px 28px", fontSize: 16 }} onClick={() => setCheckoutSku("teams-25")}>
            <Users size={18} /> Get Started with Teams ($149/year)
          </button>
        </div>
      </div>

      <div className="features">
        <div className="feature">
          <b>2 TB Shared Encrypted Pool</b>
          <span>One massive zero-knowledge storage pool for up to 25 team members. Send up to 1 TB per transfer.</span>
        </div>
        <div className="feature">
          <b>Dual Persona: Personal &amp; Work</b>
          <span>Switch between your personal vault and team workspace in one click. Admins never see private keys.</span>
        </div>
        <div className="feature">
          <b>Certified Destruction Audit</b>
          <span>Every deleted transfer produces a cryptographically signed certificate for NDA and GDPR compliance.</span>
        </div>
      </div>

      <div style={{ marginTop: 32 }}>
        <TeamCostCalculator onSelectSku={(sku) => setCheckoutSku(sku)} />
      </div>

      {checkoutSku && (
        <CryptoCheckoutModal
          defaultSku={checkoutSku}
          allowedSkus={["teams-25", "pro-annual"]}
          title="Activate LimeShare Teams (25 Members · 2 TB)"
          onSuccess={() => setCheckoutSku(null)}
          onClose={() => setCheckoutSku(null)}
        />
      )}
    </article>
  );
}

export function ProductExceed() {
  const [checkoutSku, setCheckoutSku] = useState<ProductSkuId | null>(null);
  return (
    <article className="panel legal" style={{ maxWidth: 960, margin: "0 auto" }}>
      <div style={{ textAlign: "center", maxWidth: 680, margin: "0 auto 32px" }}>
        <span className="badge" style={{ marginBottom: 12 }}>Pay &amp; Send · No Membership</span>
        <h1 style={{ fontSize: "clamp(30px, 4.5vw, 46px)", fontWeight: 900, margin: "0 0 12px", letterSpacing: "-0.03em" }}>
          LimeShare <span style={{ color: "var(--lime)" }}>Exceed</span> — Share up to 1 TB Without Signing Up
        </h1>
        <p className="hint" style={{ fontSize: 16 }}>
          Standard transfers up to 5 GB are always free. Need to send 50 GB, 200 GB, or 1 TB once? Don't waste time becoming a member — just pay once and send.
        </p>
      </div>

      <div className="pricing-grid-3">
        {(["exceed-50g", "exceed-200g", "exceed-1tb"] as ProductSkuId[]).map((id) => {
          const p = PRODUCTS[id];
          return (
            <div key={id} className={`pricing-card${p.badge ? " popular" : ""}`}>
              {p.badge && <div className="popular-ribbon">{p.badge}</div>}
              <div>
                <h3 style={{ margin: "0 0 4px", fontSize: 20 }}>{p.name}</h3>
                <div style={{ fontSize: 38, fontWeight: 900, color: "var(--lime)", margin: "14px 0" }}>
                  ${p.priceUsd.toFixed(2)}
                </div>
                <p className="hint" style={{ marginBottom: 18 }}>{p.description}</p>
                <ul style={{ listStyle: "none", padding: 0, textAlign: "left", display: "flex", flexDirection: "column", gap: 8, marginBottom: 22 }}>
                  <li><Check size={14} color="var(--lime)" /> Up to {Math.round(p.maxTransferBytes / 1024 ** 3)} GB transfer</li>
                  <li><Check size={14} color="var(--lime)" /> Up to 2,500 files / folders</li>
                  <li><Check size={14} color="var(--lime)" /> Zero-Knowledge AES-256-GCM</li>
                  <li><Check size={14} color="var(--lime)" /> No account required</li>
                </ul>
              </div>
              <button className="btn block" onClick={() => setCheckoutSku(id)}>
                Get Instant Pass
              </button>
            </div>
          );
        })}
      </div>

      {checkoutSku && (
        <CryptoCheckoutModal
          defaultSku={checkoutSku}
          allowedSkus={["exceed-50g", "exceed-200g", "exceed-1tb"]}
          onSuccess={() => setCheckoutSku(null)}
          onClose={() => setCheckoutSku(null)}
        />
      )}
    </article>
  );
}

export function ProductExtend() {
  const [checkoutSku, setCheckoutSku] = useState<ProductSkuId | null>(null);
  return (
    <article className="panel legal" style={{ maxWidth: 960, margin: "0 auto" }}>
      <div style={{ textAlign: "center", maxWidth: 680, margin: "0 auto 32px" }}>
        <span className="badge" style={{ marginBottom: 12 }}>KeepAlive &amp; Recipient Rescue</span>
        <h1 style={{ fontSize: "clamp(30px, 4.5vw, 46px)", fontWeight: 900, margin: "0 0 12px", letterSpacing: "-0.03em" }}>
          LimeShare <span style={{ color: "var(--lime)" }}>Extend</span> — Keep Files Alive for 1 Year
        </h1>
        <p className="hint" style={{ fontSize: 16 }}>
          Keep any transfer up to 5 GB online for 6 months ($1.99) or 1 full year ($2.99) with unlimited downloads. Works for both senders and recipients with zero registration.
        </p>
      </div>

      <div className="pricing-grid-3" style={{ maxWidth: 680, margin: "0 auto" }}>
        {(["extend-6m", "extend-1y"] as ProductSkuId[]).map((id) => {
          const p = PRODUCTS[id];
          return (
            <div key={id} className={`pricing-card${p.badge ? " popular" : ""}`}>
              {p.badge && <div className="popular-ribbon">{p.badge}</div>}
              <div>
                <h3 style={{ margin: "0 0 4px", fontSize: 20 }}>{p.name}</h3>
                <div style={{ fontSize: 38, fontWeight: 900, color: "var(--lime)", margin: "14px 0" }}>
                  ${p.priceUsd.toFixed(2)}
                </div>
                <p className="hint" style={{ marginBottom: 18 }}>{p.description}</p>
              </div>
              <button className="btn block" onClick={() => setCheckoutSku(id)}>
                Extend Storage Now
              </button>
            </div>
          );
        })}
      </div>

      {checkoutSku && (
        <CryptoCheckoutModal
          defaultSku={checkoutSku}
          allowedSkus={["extend-6m", "extend-1y", "vault-pass-15"]}
          onSuccess={() => setCheckoutSku(null)}
          onClose={() => setCheckoutSku(null)}
        />
      )}
    </article>
  );
}
